import styles from './Attributes.module.less'
import React, { useCallback, useEffect, useRef } from 'react'
import { AttributesBodyPropsType, AttributeValueType } from 'Types/Attribute.types'
import { InputType, TaskAttribute, TaskMode } from 'Types/Tasks.types'
import { AttributesContent } from 'Components/tasks/Attributes/AtributesContent'
import { AttributeHeaderTitle } from './AttributeHeaderTitle'
import { Feedback } from 'Components/common/Feedback/Feedback'
import { AttributeFeedbackType } from 'Types/Exams.types'
import { FeedbackPreview } from 'Pages/ExamPreview/FeedbackPreview/FeedbackPreview'
import { getDefaultAttributeValue, showAttribute } from 'Utils/attributes.utils.ts'

export const AttributesBody = (props: Omit<AttributesBodyPropsType, 'setForm'>) => {
    const toUpdateRef = useRef<Record<string, AttributeValueType | undefined>>({})

    useEffect(() => {
        Object.entries(toUpdateRef.current).forEach(([name, value]) => {
            props.form.setFieldValue([name], value)
            props.onUpdateAttribute(name, value)
        })

        toUpdateRef.current = {}
    }, [props])

    const getCorrectAnswer = (
        correctAnswer: AttributeFeedbackType['correct_answer'],
        inputType?: InputType
    ): string => {
        if (inputType === InputType.FILE) {
            return 'Sample File upload'
        }
        return Array.isArray(correctAnswer)
            ? correctAnswer.join(', ')
            : (correctAnswer ?? '').toString()
    }

    const feedbacks = props.getFeedback?.()
    const isTrainingMode = props.isTrainingMode?.()

    const renderFeedback = useCallback(
        (attribute: TaskAttribute, feedback: AttributeFeedbackType) => (
            <div
                data-testid={`feedback-${attribute.attribute_id}`}
                className={styles.feedbackContainer}
            >
                <Feedback
                    key={feedback.id}
                    isCorrect={feedback.is_correct}
                    correctAnswer={getCorrectAnswer(feedback.correct_answer)}
                    feedback={feedback.feedback}
                />
            </div>
        ),
        []
    )

    const renderFeedbackPreview = useCallback(
        (
            attribute: TaskAttribute,
            correctAnswerFeedback: AttributeFeedbackType | undefined,
            incorrectAnswerFeedback: AttributeFeedbackType | undefined,
            isTrainingMode: boolean
        ) => (
            <div
                data-testid={`feedback-preview-${attribute.attribute_id}`}
                className={styles.feedbackPreviewContainer}
            >
                <FeedbackPreview
                    correctAnswer={getCorrectAnswer(
                        correctAnswerFeedback?.correct_answer,
                        attribute.input_definition.input_type
                    )}
                    correctAnswerFeedback={correctAnswerFeedback?.feedback}
                    incorrectAnswerFeedback={incorrectAnswerFeedback?.feedback}
                    isTrainingMode={isTrainingMode}
                />
            </div>
        ),
        []
    )

    const getFeedbackComponent = useCallback(
        (attribute: TaskAttribute) => {
            if (feedbacks && feedbacks.length) {
                if (props.mode !== TaskMode.Viewer) {
                    const feedback = feedbacks.find(
                        feedback => feedback.id === attribute.version_id
                    )
                    if (feedback) {
                        return renderFeedback(attribute, feedback)
                    }
                } else {
                    const correctAnswerFeedback = feedbacks.find(
                        feedback => feedback.id === attribute.attribute_id && feedback.is_correct
                    )
                    const incorrectAnswerFeedback = feedbacks.find(
                        feedback => feedback.id === attribute.attribute_id && !feedback.is_correct
                    )
                    if (correctAnswerFeedback || incorrectAnswerFeedback) {
                        return renderFeedbackPreview(
                            attribute,
                            correctAnswerFeedback,
                            incorrectAnswerFeedback,
                            !!isTrainingMode
                        )
                    }
                }
            }
            return null
        },
        [feedbacks, isTrainingMode, props.mode, renderFeedback, renderFeedbackPreview]
    )

    if (props.attributes.length === 0) {
        return null
    }

    return (
        <div className={'attributes-body ' + (props.wrapperClassName ?? '')}>
            {props.headerComponent ?? <AttributeHeaderTitle />}
            {[...props.attributes].map((attribute: TaskAttribute) => {
                if (
                    !showAttribute(
                        props.attributes,
                        attribute,
                        props.attributesOnUI,
                        props.getValues?.() ?? {},
                        toUpdateRef
                    )
                ) {
                    delete props.attributesOnUI[attribute.name]
                    return null
                }
                props.attributesOnUI[attribute.name] = 1

                return (
                    <React.Fragment key={attribute.attribute_id + attribute.name}>
                        <AttributesContent
                            attribute={attribute}
                            value={
                                props.getValues?.()?.[attribute.name]?.data?.value ??
                                getDefaultAttributeValue(attribute.value_definition.value_type)
                            }
                            onUpdateAttribute={(name: string, value: AttributeValueType) => {
                                if (attribute.is_value_editable) {
                                    props.onUpdateAttribute(name, value)
                                }
                            }}
                            isReadOnly={props.isReadOnly}
                            taskId={props.taskId}
                        />
                        {getFeedbackComponent(attribute)}
                    </React.Fragment>
                )
            })}
        </div>
    )
}
